import { useLocalStorage } from '@hooks'
import { Campaign, DynamicVariables } from '@interfaces'
import { useEffect, useState } from 'react'

interface UseAnalyticsSetupProps {
  campaign: Campaign
  dynamicVariables: DynamicVariables
}

interface AnalyticsSetupReturn {
  prospectTrackVariables: DynamicVariables
  setProspectTrackVariables: (variables: DynamicVariables) => void
  analitycsIdentityValue: any
  setAnalitycsIdentityValue: (value: any) => void
  analitycsCrmValue: any
  setAnalitycsCrmValue: (value: any) => void
  analitycsIdentityValueUserLogged: any
  setAnalitycsIdentityValueUserLogged: (value: any) => void
}

export const useAnalyticsSetup = ({
  campaign,
  dynamicVariables
}: UseAnalyticsSetupProps): AnalyticsSetupReturn => {
  const [prospectTrackVariables, setProspectTrackVariables] = useState<DynamicVariables>(dynamicVariables)
  
  const [analitycsIdentityValue, setAnalitycsIdentityValue] = useLocalStorage(
    'ahoyData',
    {}
  )
  const [analitycsCrmValue, setAnalitycsCrmValue] = useLocalStorage(
    'crmData',
    {}
  )
  const [
    analitycsIdentityValueUserLogged,
    setAnalitycsIdentityValueUserLogged,
  ] = useLocalStorage('ahoyDataUserLogged', {})

  const saveSendsparkUserLoggedBeforeOverride = () => {
    if (!!(analitycsIdentityValue as any).userId) {
      setAnalitycsIdentityValueUserLogged(analitycsIdentityValue)
    }
  }

  const recoveryUserLoggedIfExist = () => {
    if ((analitycsIdentityValueUserLogged as any).userId) {
      setAnalitycsIdentityValue(analitycsIdentityValueUserLogged)
      setAnalitycsIdentityValueUserLogged({})
    }
  }

  const createAndSetAnalitycsCrmValue = (crmDataToMap: object): void => {
    // First that all we need to filter the crm data
    const isInvalidValue = /[!#$%^*()_+\-=\\[\]{};:"\\|/?~]/
    let crmData = {} as any

    for (const [key, value] of Object.entries(crmDataToMap)) {
      if (!isInvalidValue.test(value as string)) {
        crmData[key] = value
      }
    }
    
    // If Sendspark user exist we save this data to recovery when user is not identify by params
    saveSendsparkUserLoggedBeforeOverride()

    // Using name if exists or fistname if exist if not we take as name the part of email before @
    const validName =
      crmData?.name && crmData?.name !== '' ? crmData.name : null
    const name =
      validName ??
      crmData?.firstname ??
      (crmData?.email as string)?.split('@')[0] ??
      crmData?.email

    // We override user identity using data provided by params
    setAnalitycsCrmValue({
      name: name,
      email: crmData?.email?.toLowerCase(),
      lastName: crmData?.lastname,
      companyName: crmData?.companyname,
    })
  }

  useEffect(() => {
    if (campaign?.featureFlips?.dynamicVariables && dynamicVariables)
      setProspectTrackVariables(dynamicVariables)

    if (campaign?.dynamicVideoProps) {
      const variables: DynamicVariables = {
        email: campaign?.dynamicVideoProps?.contact?.email,
      }

      campaign?.dynamicVideoProps?.customVariables?.forEach(variable => {
        if (variable.value) variables[variable.name] = variable.value
      })

      setProspectTrackVariables(variables)
    }
  }, [dynamicVariables, campaign?.dynamicVideoProps])

  useEffect(() => {
    if (campaign?.featureFlips?.dynamicVariables) {
      if (
          campaign?.featureFlips?.dynamicsExtraFields &&
          campaign?.dynamicVideoProps?.customVariables
        ) {
        const contact: Record<string, string> = {}
        campaign?.dynamicVideoProps?.customVariables?.forEach((k: {name: string, value: string}) => {
          contact[k.name] = k.value
        })
        createAndSetAnalitycsCrmValue(contact)
      } else if (campaign?.dynamicVideoProps?.contact) {
        const { contact } = campaign.dynamicVideoProps
        createAndSetAnalitycsCrmValue(contact)
      } else if (
        dynamicVariables?.email ||
        dynamicVariables?.firstname ||
        dynamicVariables?.lastname ||
        dynamicVariables?.companyname
      ) {
        createAndSetAnalitycsCrmValue(dynamicVariables)
      } else {
        // We recovery Sendspark user data because
        recoveryUserLoggedIfExist()
      }
    }

    return () => {
      setAnalitycsIdentityValue({})
    }
  }, [])

  return {
    prospectTrackVariables,
    setProspectTrackVariables,
    analitycsIdentityValue,
    setAnalitycsIdentityValue,
    analitycsCrmValue,
    setAnalitycsCrmValue,
    analitycsIdentityValueUserLogged,
    setAnalitycsIdentityValueUserLogged
  }
}